import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Container,
  Box,
  Chip,
  Stack,
  Button,
  Paper,
} from "@mui/material";
import {
  Code,
  PhoneIphone,
  BusinessCenter,
  CloudQueue,
  BugReport,
  Shield,
  ArrowOutwardRounded as ArrowOutwardRoundedIcon,
} from "@mui/icons-material";
import PageTransition from "./PageTransition";

const services = [
  {
    title: "Product & Fullstack Engineering",
    description:
      "React, Node, .NET, Java, mobile, and data squads owning delivery from MVP discovery to global rollouts with DevSecOps baked in.",
    icon: <Code />,
    tags: ["Design systems", "Micro frontends", "API-first"],
  },
  {
    title: "Mobile & Experience Platforms",
    description:
      "Human-centred UX, accessibility audits, and high-performance PWAs or native apps that keep your customers loyal.",
    icon: <PhoneIphone />,
    tags: ["CX research", "Prototyping", "Motion design"],
  },
  {
    title: "Business & Data Solutions",
    description:
      "Composable ERP/CRM, AI assistants, data lakes, and workflow automation aligning every department around trusted intelligence.",
    icon: <BusinessCenter />,
    tags: ["Data mesh", "RPA", "BI dashboards"],
  },
  {
    title: "Cloud & Platform Reliability",
    description:
      "Cloud strategy, FinOps, SRE pods, and observability frameworks across AWS, Azure, and GCP with zero-downtime releases.",
    icon: <CloudQueue />,
    tags: ["IaC", "Kubernetes", "24/7 Ops"],
  },
  {
    title: "Quality Engineering",
    description:
      "Automation, performance, and compliance testing programs that catch risk early and protect customer trust.",
    icon: <BugReport />,
    tags: ["TestOps", "Performance", "Regulatory"],
  },
  {
    title: "Cybersecurity & Resilience",
    description:
      "Threat modelling, SOC automation, zero-trust architecture, and incident response to keep fast-moving teams secure.",
    icon: <Shield />,
    tags: ["Zero trust", "Pen testing", "SOC"],
  },
];

const OurServices = () => {
  return (
    <Box component="section" id="services" sx={{ backgroundColor: "#f4f6fb", py: { xs: 8, md: 10 } }}>
      <PageTransition>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={2} alignItems="flex-start">
                <Chip
                  label="What we deliver"
                  sx={{ fontWeight: 600, letterSpacing: "0.08em", backgroundColor: "rgba(20,184,166,0.15)" }}
                />
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  Outcomes-first services that integrate strategy, design, engineering, and security.
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                  From embedded squads to managed services, MC3 Smart Tech adapts to your operating rhythm while keeping KPIs
                  transparent. Mix and match capabilities without losing accountability.
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: "1px solid rgba(15,23,42,0.08)",
                  backgroundColor: "#ffffff",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Engagement models
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {["Discovery & strategy intensives", "Dedicated outcome squads", "24/7 managed operations"].map(
                    (item) => (
                      <Stack key={item} direction="row" spacing={1.2} alignItems="center">
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #14b8a6, #0ea5e9)",
                          }}
                        />
                        <Typography variant="body2" color="text.primary">
                          {item}
                        </Typography>
                      </Stack>
                    )
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 6,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 3,
            }}
          >
            {services.map((service) => (
              <Card
                key={service.title}
                sx={{
                  height: "100%",
                  borderRadius: 4,
                  border: "1px solid rgba(15,23,42,0.08)",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 20px 45px rgba(15,23,42,0.08)",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #0f172a, #312e81)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                      }}
                      aria-hidden
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {service.title}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 2.5, color: "text.secondary", lineHeight: 1.7 }}>
                    {service.description}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ mt: 3 }}>
                    {service.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          backgroundColor: "rgba(20,184,166,0.12)",
                          color: "#0f172a",
                        }}
                      />
                    ))}
                  </Stack>
                  <Button
                    component="a"
                    href="/contact-us"
                    sx={{ mt: 3, fontWeight: 600 }}
                    endIcon={<ArrowOutwardRoundedIcon fontSize="small" />}
                  >
                    Discuss this service
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </PageTransition>
    </Box>
  );
};

export default OurServices;
